import { useMemo } from 'react'
import { Space, Typography } from 'antd'
import { alphabeticalCompare } from '@cims-online/shared'
import { CIMSTag } from 'components/CIMSTag'
import { CIMSTags } from 'components/CIMSTags'
import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import { HelpMessage } from 'utils/constants'
import PermissionConditions from '../components/PermissionConditions'
import { usePermissionsContext } from '../../../PermissionsContext'
import {
  getPermissionActionMetadata,
  permissionActionMetadata,
} from '../../../metadata'
import { WorkflowPermissionRecord } from '../../../types'
import { permissionConditionSearchText } from '../../../utils'

const help = HelpMessage.CustomWorkflows.Permissions.Columns

export function useColumns() {
  const {
    metadata: { fieldMap, statusMap },
    users: { byId: usersById, label: userLabel, tooltip: userTooltip },
  } = usePermissionsContext()
  return useMemo<CIMSColumn<WorkflowPermissionRecord>[]>(
    () => [
      {
        title: 'Permission',
        width: 275,
        help: help.Permission,
        filter: {
          with: 'callback',
          items: Object.entries(permissionActionMetadata).map(
            ([value, { label }]) => ({ value, text: label })
          ),
          onFilter: (value, { action }) => action === value,
        },
        sort: {
          with: 'callback',
          default: 'ascend',
          onCompare: (a, b) =>
            alphabeticalCompare(
              getPermissionActionMetadata(a.action).label,
              getPermissionActionMetadata(b.action).label
            ),
        },
        render: ({ action }) => {
          const { description, label } = getPermissionActionMetadata(action)
          return (
            <Space direction="vertical" size={3}>
              <CIMSTag>{label}</CIMSTag>
              <Typography.Text type="secondary">{description}</Typography.Text>
            </Space>
          )
        },
      },
      {
        title: 'Users',
        width: 325,
        help: help.Users,
        filter: {
          type: 'search',
          with: 'callback',
          onFilter: (value, { users }) =>
            users
              .map(userLabel)
              .join(' ')
              .toLowerCase()
              .includes(value.toString().toLowerCase()),
        },
        render: ({ users }) => (
          <CIMSTags
            tags={users.map((id) => ({
              text: userLabel(id),
              tooltip: userTooltip(id),
              strikethrough: !usersById.has(id),
            }))}
          />
        ),
      },
      {
        title: 'Conditions',
        width: 375,
        help: help.Conditions,
        filter: {
          type: 'search',
          with: 'callback',
          onFilter: (value, record) =>
            permissionConditionSearchText(record, fieldMap, statusMap)
              .toLowerCase()
              .includes(value.toString().toLowerCase()),
        },
        render: (record) => <PermissionConditions record={record} />,
      },
    ],
    [fieldMap, statusMap, userLabel, userTooltip, usersById]
  )
}
